import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ArrowForward, InsertChartOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { MARGIN_STORY } from './missionData';
import { s, cardSx, focusRing, reduceMotion } from './missionTokens';

/**
 * Business Story — an AI explanation of a movement, with its evidence.
 *
 * This is the section that has to carry "reports explain the business": a
 * number with no attribution is the legacy-ERP failure mode. So the card
 * states the movement, decomposes the drivers that produced it, names the
 * single largest contributor with its rupee impact, and offers the three
 * actions a manager would actually take next.
 */
const BusinessStoryCard: React.FC = () => {
  const navigate = useNavigate();
  const max = Math.max(...MARGIN_STORY.drivers.map((d) => Math.abs(d.delta)));

  return (
    <Box sx={{ ...cardSx, p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
        <Typography sx={{ fontSize: '0.98rem', fontWeight: 800, color: s.heading }}>Business Story</Typography>
        <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, backgroundColor: s.successSoft }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: s.success }}>NEW</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box
          component="button"
          onClick={() => navigate('/reports')}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 0, background: 'none',
            cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: s.primary, borderRadius: 1,
            '& .arw': { transition: 'transform .25s ease' },
            '&:hover .arw': { transform: 'translateX(4px)' },
            ...focusRing, ...reduceMotion,
          }}
        >
          View All
          <ArrowForward className="arw" sx={{ fontSize: 15 }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.25, mb: 2 }}>
        <Box
          sx={{
            width: 34, height: 34, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center',
            backgroundColor: s.primarySoft,
          }}
        >
          <InsertChartOutlined sx={{ fontSize: 18, color: s.primary }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: s.heading, lineHeight: 1.4 }}>
            {MARGIN_STORY.question}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: s.muted, mt: 0.25 }}>
            AI-generated analysis based on your real data
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1.15fr 0.85fr' },
          gap: 2,
          alignItems: 'start',
          flex: 1,
        }}
      >
        {/* Driver decomposition — bar length is share of the largest mover, so
            the dominant driver is obvious before any number is read. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {MARGIN_STORY.drivers.map((d, i) => {
            const isCost = d.label !== 'Revenue';
            const color = isCost ? s.danger : s.success;
            return (
              <Box key={d.label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: s.body, fontWeight: 500 }}>{d.label}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color }}>
                    +{d.delta}%
                  </Typography>
                </Box>
                <Box sx={{ height: 5, borderRadius: 3, backgroundColor: s.raised, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      backgroundColor: color,
                      width: `${(Math.abs(d.delta) / max) * 100}%`,
                      animation: `storyBar .8s cubic-bezier(.2,.7,.2,1) ${i * 90}ms both`,
                      '@keyframes storyBar': { from: { width: 0 }, to: { width: `${(Math.abs(d.delta) / max) * 100}%` } },
                      ...reduceMotion,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: s.dangerSoft,
            border: `1px solid ${s.danger}22`,
          }}
        >
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: s.body, mb: 0.5 }}>
            Primary driver
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: s.heading, lineHeight: 1.3 }}>
            {MARGIN_STORY.primaryDriver.label}
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: s.danger, mt: 0.5 }}>
            {MARGIN_STORY.primaryDriver.impact}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        {MARGIN_STORY.actions.map((a) => (
          <Button
            key={a}
            size="small"
            variant="outlined"
            onClick={() => navigate('/reports')}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 700,
              borderRadius: 2,
              color: s.body,
              borderColor: s.border,
              '&:hover': { borderColor: s.primary, backgroundColor: s.primarySoft, color: s.primary },
              ...focusRing,
            }}
          >
            {a}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default BusinessStoryCard;
